import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import { toast } from "sonner";
import { UNASSIGNED_COLOR, OUTLET_COLOR, ENDPOINT_COLOR } from "../../../lib/routeColors";
import { apiClient } from "../../../lib/apiClient";
import brandColors from "../../../brandColors";

// Road-following geometry for the (unsaved) builder routes. Posts each route's
// ordered waypoints to the ad-hoc OSRM endpoint. Debounced so dragging/reordering
// stops doesn't hammer OSRM — recomputes ~400ms after the ordering settles.
// Straight-line fallback (handled at render) while loading or when OSRM is down.
function useBuilderRoadLegs(routes) {
  const [legsByTempId, setLegsByTempId] = useState({});
  const timerRef = useRef(null);
  const sig = routes.map((r) => `${r.tempId}#${r.coords.map((c) => c.join(",")).join(";")}`).join("|");
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    let cancelled = false;
    timerRef.current = setTimeout(async () => {
      const results = await Promise.all(
        routes.map(async (r) => {
          if (r.coords.length < 2) return [r.tempId, null];
          try {
            const { data } = await apiClient.post("/route-geometry", { coordinates: r.coords });
            return [r.tempId, data?.legs || null];
          } catch {
            return [r.tempId, null];
          }
        })
      );
      if (!cancelled) setLegsByTempId(Object.fromEntries(results));
    }, 400);
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);
  return legsByTempId;
}

// Dropping a dragged stop within this many meters of another route's stop
// is treated as "move it to that route" — far enough to avoid mis-fires
// between adjacent addresses, close enough to be an easy, deliberate drop.
const DRAG_DROP_THRESHOLD_M = 500;

function findNearestOtherRouteDelivery(draggedId, draggedOwnerTempId, deliveries, ownerMap, dragLatLng) {
  let best = null;
  for (const d of deliveries) {
    if (d.id === draggedId || !d.latitude || !d.longitude) continue;
    const owner = ownerMap.get(d.id);
    if (!owner || owner === draggedOwnerTempId) continue;
    const dist = dragLatLng.distanceTo(L.latLng(parseFloat(d.latitude), parseFloat(d.longitude)));
    if (dist <= DRAG_DROP_THRESHOLD_M && (!best || dist < best.dist)) best = { ownerTempId: owner, dist };
  }
  return best;
}

// Cached by (color,label,shape,size) so react-leaflet sees a stable icon object
// identity across re-renders and doesn't call marker.setIcon() (which was
// destroying the popup's click binding) unless the marker's own color/size
// actually changed.
const iconCache = new Map();

function divIcon(color, label, shape = "circle", size = 22, testId = "", highlight = false) {
  const key = `${color}|${label}|${shape}|${size}|${testId}|${highlight}`;
  if (iconCache.has(key)) return iconCache.get(key);
  const radius = shape === "circle" ? "50%" : "4px";
  const border = highlight ? "3px solid #FBA022" : "2px solid white";
  const shadow = highlight ? "0 0 0 5px rgba(245,199,84,0.35), 0 1px 4px rgba(0,0,0,0.45)" : "0 1px 4px rgba(0,0,0,0.45)";
  const icon = L.divIcon({
    className: "",
    html: `<div ${testId ? `data-testid="${testId}"` : ""} style="background:${color};width:${size}px;height:${size}px;border-radius:${radius};border:${border};box-shadow:${shadow};display:flex;align-items:center;justify-content:center;color:white;font-size:10px;font-weight:700;font-family:IBM Plex Sans,sans-serif;">${label || ""}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
  iconCache.set(key, icon);
  return icon;
}

function previewIcon(color, label, testId = "") {
  const key = `preview|${color}|${label}|${testId}`;
  if (iconCache.has(key)) return iconCache.get(key);
  const icon = L.divIcon({
    className: "",
    html: `<div ${testId ? `data-testid="${testId}"` : ""} style="width:26px;height:26px;border-radius:50%;background:${color}33;border:3px dashed ${color};display:flex;align-items:center;justify-content:center;color:${color};font-size:11px;font-weight:800;font-family:IBM Plex Sans,sans-serif;">${label || ""}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    popupAnchor: [0, -13],
  });
  iconCache.set(key, icon);
  return icon;
}

function FitBounds({ points }) {
  const map = useMap();
  const didFit = useRef(false);
  useEffect(() => {
    if (didFit.current || points.length === 0) return;
    try {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 15, animate: false });
      didFit.current = true;
    } catch {
      /* invalid bounds, ignore */
    }
  }, [points, map]);
  return null;
}

function DrawController({ active, onPolygonComplete }) {
  const map = useMap();
  const handlerRef = useRef(null);

  useEffect(() => {
    const onCreated = (e) => {
      const latlngs = e.layer.getLatLngs()[0].map((ll) => [ll.lat, ll.lng]);
      onPolygonComplete(latlngs);
      map.removeLayer(e.layer);
    };
    map.on(L.Draw.Event.CREATED, onCreated);
    return () => map.off(L.Draw.Event.CREATED, onCreated);
  }, [map, onPolygonComplete]);

  useEffect(() => {
    if (active) {
      const handler = new L.Draw.Polygon(map, {
        showArea: false, allowIntersection: false,
        shapeOptions: { color: brandColors.PRIMARY, weight: 2, fillOpacity: 0.08 },
      });
      handler.enable();
      handlerRef.current = handler;
    } else if (handlerRef.current) {
      handlerRef.current.disable();
      handlerRef.current = null;
    }
    return () => { if (handlerRef.current) { handlerRef.current.disable(); handlerRef.current = null; } };
  }, [active, map]);

  return null;
}

// Parses the outlet's saved delivery-zone GeoJSON polygon (same shape drawn
// in Settings > Delivery Zone) into Leaflet [lat,lng] pairs, so the manager
// sees the coverage boundary shaded on the builder map without having to
// jump to Settings first.
function deliveryZoneLatLngs(area) {
  try {
    const parsed = typeof area === "string" ? JSON.parse(area) : area;
    // Accepts a bare Polygon, a Feature<Polygon>, or a FeatureCollection whose
    // first feature is a Polygon — the outlet's saved zone has appeared in
    // more than one of these shapes across imports/tools.
    const geometry = parsed?.type === "FeatureCollection" ? parsed?.features?.[0]?.geometry
      : parsed?.type === "Feature" ? parsed?.geometry
      : parsed;
    const ring = geometry?.coordinates?.[0];
    if (!Array.isArray(ring) || ring.length < 3) return null;
    return ring.map(([lng, lat]) => [lat, lng]);
  } catch {
    return null;
  }
}

function PanToHover({ deliveryId, deliveries }) {
  const map = useMap();
  useEffect(() => {
    if (!deliveryId) return;
    const d = deliveries.find((x) => x.id === deliveryId);
    if (!d || !d.latitude || !d.longitude) return;
    map.panTo([parseFloat(d.latitude), parseFloat(d.longitude)], { animate: true });
  }, [deliveryId, deliveries, map]);
  return null;
}

export function BuilderMap({
  outlet, deliveries, ownerMap, tempRoutes, activeTempId, drawActive,
  onPolygonComplete, onMarkerAction, onMarkerDrop, onSelectRoute, onSelectSavedRoute,
  highlightedTempId, previewEndpoints, hoveredDeliveryId,
  routeColorMap, editingRouteIds,
}) {
  const colorById = useMemo(() => {
    const m = new Map();
    tempRoutes.forEach((r) => m.set(r.tempId, r.color));
    return m;
  }, [tempRoutes]);

  const activeRoute = tempRoutes.find((r) => r.tempId === activeTempId) || null;
  const activeDriverEndpoint = activeRoute?.driverEndpoint || null;

  const allPoints = useMemo(() => {
    const pts = deliveries
      .filter((d) => d.latitude && d.longitude && d.latitude !== "0")
      .map((d) => [parseFloat(d.latitude), parseFloat(d.longitude)]);
    if (outlet?.latitude && outlet?.longitude) pts.push([parseFloat(outlet.latitude), parseFloat(outlet.longitude)]);
    return pts;
  }, [deliveries, outlet]);

  const center = allPoints[0] || [53.5461, -113.4938];
  const deliveryZone = useMemo(() => deliveryZoneLatLngs(outlet?.area), [outlet]);

  // Optimized routes with their ordered waypoints ([lon,lat], as the optimizer
  // emits them) for road-geometry fetching + straight-line fallback positions.
  const roadRoutes = useMemo(
    () => tempRoutes
      .filter((r) => r.optimized?.steps?.length)
      .map((r) => ({ tempId: r.tempId, color: r.color, coords: r.optimized.steps.map((s) => s.location) })),
    [tempRoutes]
  );
  const legsByTempId = useBuilderRoadLegs(roadRoutes);

  const handleDragEnd = (d, ownerTempId, e) => {
    const marker = e.target;
    const dragLatLng = marker.getLatLng();
    // The address itself never moves — always snap the pin back, the drag is
    // just a gesture to pick a target route, not to relocate the delivery.
    marker.setLatLng(L.latLng(parseFloat(d.latitude), parseFloat(d.longitude)));
    const match = findNearestOtherRouteDelivery(d.id, ownerTempId, deliveries, ownerMap, dragLatLng);
    if (match) {
      onMarkerDrop(d.id, match.ownerTempId);
    } else {
      toast.info("Drop it right on top of another route's stop to move it there.");
    }
  };

  return (
    <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%" }} data-testid="route-builder-map">
      <TileLayer
        attribution='&copy; OpenStreetMap contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FitBounds points={allPoints} />
      <DrawController active={drawActive && !!activeRoute} onPolygonComplete={onPolygonComplete} />
      <PanToHover deliveryId={hoveredDeliveryId} deliveries={deliveries} />

      {deliveryZone && (
        <Polygon
          positions={deliveryZone}
          pathOptions={{ color: brandColors.PRIMARY, weight: 2, dashArray: "6 6", fillColor: brandColors.PRIMARY, fillOpacity: 0.07 }}
          interactive={false}
          data-testid="builder-delivery-zone-overlay"
        />
      )}

      {outlet?.latitude && outlet?.longitude && (
        <Marker position={[parseFloat(outlet.latitude), parseFloat(outlet.longitude)]} icon={divIcon(OUTLET_COLOR, "R", "square", 26, "outlet-marker")}>
          <Popup><p className="text-xs font-semibold">Restaurant — {outlet.name}</p><p className="text-xs">{outlet.address}</p></Popup>
        </Marker>
      )}

      {activeDriverEndpoint?.latitude && activeDriverEndpoint?.longitude && (
        <Marker position={[parseFloat(activeDriverEndpoint.latitude), parseFloat(activeDriverEndpoint.longitude)]} icon={divIcon(ENDPOINT_COLOR, "D", "square", 24, "driver-endpoint-marker")}>
          <Popup><p className="text-xs font-semibold">Driver Endpoint</p><p className="text-xs">{activeDriverEndpoint.address}</p></Popup>
        </Marker>
      )}

      {(previewEndpoints || []).filter((p) => p.latitude && p.longitude).map((p) => (
        <Marker
          key={`preview-${p.driverId}`}
          position={[parseFloat(p.latitude), parseFloat(p.longitude)]}
          icon={previewIcon(p.color || ENDPOINT_COLOR, (p.name || "?").charAt(0).toUpperCase(), `driver-preview-marker-${p.driverId}`)}
          zIndexOffset={500}
        >
          <Popup>
            <div className="text-xs space-y-0.5" data-testid={`preview-endpoint-popup-${p.driverId}`}>
              <p className="font-semibold text-text-main">{p.name} — candidate driver</p>
              <p className="text-text-muted">{p.address}</p>
            </div>
          </Popup>
        </Marker>
      ))}

      {deliveries.filter((d) => d.latitude && d.longitude && d.latitude !== "0").map((d) => {
        const ownerTempId = ownerMap.get(d.id);
        // A delivery still committed to ANOTHER saved route not currently open for
        // editing is "locked" — shown for context but not assignable in this session.
        const isLocked = !ownerTempId && d.route_id && !(editingRouteIds && editingRouteIds.has(d.route_id));
        const color = ownerTempId ? colorById.get(ownerTempId) : isLocked ? (routeColorMap?.get(d.route_id) || UNASSIGNED_COLOR) : UNASSIGNED_COLOR;
        const isActiveOwned = ownerTempId === activeTempId;
        const isHovered = d.id === hoveredDeliveryId;
        const dimmed = !isHovered && highlightedTempId && ownerTempId && ownerTempId !== highlightedTempId;
        // Clicking a pin selects its route (opens the popup too). Owned pins
        // select the open route; locked pins open+select the saved route.
        const markerHandlers = {};
        if (ownerTempId) {
          markerHandlers.dragend = (e) => handleDragEnd(d, ownerTempId, e);
          markerHandlers.click = () => onSelectRoute?.(ownerTempId);
        } else if (isLocked && d.route_id) {
          markerHandlers.click = () => onSelectSavedRoute?.(d.route_id);
        }
        return (
          <Marker
            key={d.id}
            position={[parseFloat(d.latitude), parseFloat(d.longitude)]}
            icon={divIcon(color, "", "circle", isHovered ? 26 : (dimmed ? 12 : 16), `delivery-marker-${d.id}`, isHovered)}
            opacity={dimmed ? 0.35 : (isLocked ? 0.6 : 1)}
            zIndexOffset={isHovered ? 1000 : 0}
            draggable={!!ownerTempId && !drawActive}
            eventHandlers={markerHandlers}
          >
            <Popup>
              <div className="text-xs space-y-1" data-testid={`delivery-popup-${d.id}`}>
                <p className="font-semibold text-text-main">{d.customer_name || `Delivery #${d.id}`}</p>
                <p className="text-text-muted">{d.address || "No address"}</p>
                <p className="text-text-muted">Sub #{d.subscription_id} · {d.plan_name || "—"}</p>
                {isActiveOwned ? (
                  <button
                    className="mt-1 rounded bg-danger px-2 py-1 text-[11px] font-semibold text-white"
                    onClick={() => onMarkerAction(d.id, "remove")}
                    data-testid={`marker-remove-${d.id}`}
                  >Remove from Route {activeTempId}</button>
                ) : isLocked ? (
                  <p className="text-[11px] font-medium text-text-muted" data-testid={`marker-locked-${d.id}`}>
                    Locked — on Route #{d.route_id} (already started or has delivered stops)
                  </p>
                ) : !ownerTempId && activeRoute ? (
                  <button
                    className="mt-1 rounded bg-primary px-2 py-1 text-[11px] font-semibold text-white"
                    onClick={() => onMarkerAction(d.id, "add")}
                    data-testid={`marker-add-${d.id}`}
                  >Add to Route {activeTempId}</button>
                ) : ownerTempId ? (
                  <p className="text-[11px] font-medium" style={{ color }}>
                    Owned by Route {ownerTempId} · drag onto another route's stop to move it
                  </p>
                ) : (
                  <p className="text-[11px] text-text-muted">Select or create a route to assign this stop.</p>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}

      {tempRoutes.filter((r) => r.optimized?.steps?.length).map((r) => {
        const straight = r.optimized.steps.map((s) => [s.location[1], s.location[0]]);
        const dimmed = highlightedTempId && r.tempId !== highlightedTempId;
        const opts = { color: r.color, weight: dimmed ? 2 : 4, opacity: dimmed ? 0.3 : 0.85, dashArray: dimmed ? "4 6" : null };
        const roadLegs = legsByTempId[r.tempId];
        // One polyline per road leg when geometry is ready; otherwise a single
        // straight polyline through the ordered waypoints (OSRM down / loading).
        if (Array.isArray(roadLegs) && roadLegs.length) {
          return roadLegs.map((leg, i) => (
            <Polyline
              key={`line-${r.tempId}-${i}`}
              positions={leg && leg.length >= 2 ? leg : [straight[i], straight[i + 1]]}
              pathOptions={opts}
              eventHandlers={{ click: () => onSelectRoute?.(r.tempId) }}
            />
          ));
        }
        return (
          <Polyline key={`line-${r.tempId}`} positions={straight}
            pathOptions={opts}
            eventHandlers={{ click: () => onSelectRoute?.(r.tempId) }}
          />
        );
      })}
    </MapContainer>
  );
}
